import { useState } from 'react'
import type { Organisation } from '../api/types'
import { radius } from '../components/primitives/shape'

/**
 * The organisations you belong to, and the way to make another.
 *
 * One person, several places: a job, a client, the repos that are nobody's
 * business but their own. They share nothing, which is the point of having more
 * than one, so this is the only crossing between them.
 *
 * A row of small tiles beside the organisation's name, at the top of the
 * sidebar and of a phone's Home.
 */
export function OrganisationRail({
  organisations, current, size, onSelect, onCreate,
}: {
  organisations: Organisation[]
  current: string | undefined
  size: number
  onSelect: (slug: string) => void
  onCreate: (name: string) => Promise<unknown>
}) {
  const [naming, setNaming] = useState(false)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)
  const large = size > 30

  function close() {
    setNaming(false)
    setName('')
    setFailure(null)
  }

  async function create() {
    const trimmed = name.trim()
    if (!trimmed || busy) return
    setBusy(true)
    setFailure(null)
    try {
      await onCreate(trimmed)
      close()
    } catch (error) {
      // Closing on a refusal would look like it worked, and the tile would be
      // missing for reasons nobody could see.
      setFailure(error instanceof Error ? error.message : 'not created')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: large ? 12 : 6, flex: 'none' }}>
      {organisations.map((organisation) => {
        const on = organisation.slug === current
        return (
          <button
            key={organisation.slug}
            onClick={() => onSelect(organisation.slug)}
            aria-label={organisation.name}
            aria-current={on}
            // On a phone's Home a tile is 34px with a 12px gap, so a 44px hit
            // area fits between two of them with 2px to spare and never steals
            // its neighbour's taps. The sidebar's 22px tiles are for a mouse,
            // where `.sp-tap` adds no hit area.
            className="sp-tap"
            style={{
              position: 'relative', width: size, height: size, flex: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              borderRadius: '50%',
              background: on ? 'var(--text)' : 'var(--panel-strong)',
              color: on ? 'var(--ground)' : 'var(--text-dim)',
              font: `600 ${large ? 11 : 9.5}px var(--font-machine)`,
              boxShadow: on ? 'none' : 'inset 0 0 0 1px var(--border-strong)',
            }}
          >
            {organisation.short_label}
            {organisation.unread > 0 && (
              <span style={{ position: 'absolute', top: -1, right: -1, width: large ? 9 : 8, height: large ? 9 : 8, borderRadius: '50%', background: 'var(--accent)', border: '2px solid var(--ground)' }} />
            )}
          </button>
        )
      })}

      <button
        onClick={() => (naming ? close() : setNaming(true))}
        aria-label="New organisation"
        aria-expanded={naming}
        className="sp-tap"
        style={{
          position: 'relative',
          width: size, height: size, flex: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          borderRadius: '50%', background: 'transparent',
          color: 'var(--text-faint)', font: `400 ${large ? 17 : 14}px var(--font-machine)`,
          boxShadow: 'inset 0 0 0 1px var(--border-strong)',
        }}
      >
        +
      </button>

      {naming && (
        <div
          role="dialog"
          aria-label="New organisation"
          onKeyDown={(event) => { if (event.key === 'Escape') close() }}
          style={{
            // Fixed, under the sidebar's first row: the tiles end at the
            // sidebar's right edge, and a panel hung from them would leave the
            // screen on a narrow column or a phone.
            position: 'fixed',
            top: 'calc(var(--safe-top) + 56px)',
            left: 'max(16px, var(--safe-left))',
            width: 'min(300px, calc(100vw - 32px))',
            zIndex: 60, padding: 14,
            display: 'flex', flexDirection: 'column', gap: 10,
            background: 'var(--ground-raised)', border: '1px solid var(--border-strong)',
            borderRadius: radius.md, boxShadow: 'var(--shadow-overlay)',
          }}
        >
          <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', textTransform: 'uppercase', color: 'var(--text-dim)' }}>
            Name
            <input
              autoFocus
              value={name}
              aria-label="Organisation name"
              maxLength={120}
              disabled={busy}
              placeholder="Side Projects"
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') void create() }}
              style={{ width: '100%', marginTop: 8, fontSize: 14, fontWeight: 600, letterSpacing: 0, textTransform: 'none', color: 'var(--text)', background: 'var(--panel-strong)', border: '1px solid var(--border-strong)', borderRadius: radius.sm, padding: '9px 11px' }}
            />
          </label>
          {failure && (
            <div role="alert" style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--deletions)' }}>
              {failure}
            </div>
          )}
          <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: 'var(--text-faint)' }}>
            It starts empty. Rooms arrive when you run{' '}
            <code style={{ font: '400 11px var(--font-machine)' }}>stagepost init</code>{' '}
            in a repository, or connect GitHub.
          </p>
          <button
            onClick={() => void create()}
            disabled={busy || !name.trim()}
            style={{
              padding: '11px 14px', minHeight: 44, borderRadius: radius.sm, cursor: 'pointer',
              background: 'var(--text)', color: 'var(--ground)',
              fontSize: 12.5, fontWeight: 700,
              opacity: busy || !name.trim() ? 0.45 : 1,
            }}
          >
            {busy ? 'Creating…' : 'Create'}
          </button>
        </div>
      )}
    </div>
  )
}
